/**
 * Scrubbing for browser telemetry (Grafana Faro). Every item passes through
 * {@link scrubItem} before it leaves the browser.
 *
 * Best effort: it removes secrets in known shapes (URL query strings, JWTs,
 * bearer tokens, `token=…`-style pairs, values under sensitive keys) and
 * email addresses. A secret with none of these shapes, e.g. a bare API key
 * in an error message, still passes through, so code must not put secrets
 * in error text.
 */

const REDACTED = "[redacted]";
const TRUNCATED = "[truncated]";
const MAX_DEPTH = 10;

/** Object keys whose values are always dropped, whatever they contain. */
const SENSITIVE_KEY = /authorization|cookie|password|secret|token|api[-_]?key/i;

const PATTERNS: Array<[RegExp, string]> = [
  // Query string and fragment of absolute URLs. The OIDC callback carries
  // ?code=…&state=…, and other pages may carry ids or tokens.
  // Stops before trailing punctuation so "…?q=1: 500" keeps ": 500".
  [
    /(https?:\/\/[^\s?#"'<>]+)[?#][^\s"'<>]*?(?=[:,;.)\]]?(?:[\s"'<>]|$))/g,
    "$1",
  ],
  // JWTs (access/id tokens) wherever they appear in text.
  [/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, "[jwt]"],
  // "Bearer <token>" in logged headers or messages.
  [/\bBearer\s+[^\s"',]+/gi, `Bearer ${REDACTED}`],
  // key=value / key: value pairs in free text, e.g. "failed, token=abc",
  // "client_secret: xyz", "password='hunter2'".
  [
    /\b([\w-]*(?:token|secret|password|passwd|api[-_]?key)[\w-]*)(\s*[=:]\s*)["']?[^\s"'&,;]+["']?/gi,
    `$1$2${REDACTED}`,
  ],
  // Email addresses (owner emails appear in emulator records).
  [/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, "[email]"],
];

/** Removes query strings, secrets and email addresses from a string. */
export function scrubString(value: string): string {
  return PATTERNS.reduce(
    (s, [re, replacement]) => s.replace(re, replacement),
    value,
  );
}

/**
 * Recursively scrubs every string in a JSON-like value. Anything nested
 * deeper than MAX_DEPTH is replaced, never sent unscrubbed.
 */
export function scrubValue<T>(value: T, depth = 0): T {
  if (depth > MAX_DEPTH) return TRUNCATED as T;
  if (typeof value === "string") return scrubString(value) as T;
  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, depth + 1)) as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value)) {
      out[key] = SENSITIVE_KEY.test(key) ? REDACTED : scrubValue(v, depth + 1);
    }
    return out as T;
  }
  return value;
}

/**
 * Faro `beforeSend` hook: scrubs the payload and metadata (page URL, view,
 * session attributes) of every transport item.
 */
export function scrubItem<T extends { payload: unknown; meta: unknown }>(
  item: T,
): T {
  return {
    ...item,
    payload: scrubValue(item.payload),
    meta: scrubValue(item.meta),
  };
}
